import { type VariantProps, cva } from "class-variance-authority";
import React from "react";

import { cn } from "~/lib/cn";

export const badgeVariants = cva(
  "inline-flex items-center gap-1 border font-semibold transition focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        "outline-primary": "text-foreground border-primary text-purple-700",
        "outline-secondary": "border-gray-200 bg-brand-50 text-text-tertiary",
        gray: "border-none bg-gray-50 text-gray-800 hover:bg-gray-100",
        black100: "border-none bg-gray-100 text-gray-900 hover:bg-gray-400/50",

        overlay:
          "border-transparent bg-gradient-to-r from-purple-700/40 to-violet-700/40 backdrop-blur text-white",
        pro: "bg-gradient-pro-orange hover:brightness-110 active:brightness-90 text-white border-orange-500",
        affirm: "bg-lime-100 text-lime-700 border-none",
        danger: "bg-red-100 text-red-700 border-none",
        pending: "bg-blue-100 text-blue-700 border-none",
        scheduled: "bg-blue-50 text-blue-700 border-blue-200",
        success: "bg-green-50 text-green-700 border-green-200",
        future: "bg-blue-50 text-blue-700 border-blue-200",
        cancelled: "bg-gray-50 text-gray-700 border-gray-200",
      },
      size: {
        sm: "px-2 py-1 text-xs leading-none",
        lg: "px-4 py-2 text-md leading-none",
        default: "px-3 py-2 text-sm leading-none",
      },
      radius: {
        full: "rounded-full",
        rounded: "rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      radius: "full",
    },
  },
);

type BadgeProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof badgeVariants>;

/** Small pill label for a status or count, with variants, sizes and radius. */
export const Badge = ({
  className,
  variant,
  size,
  radius,
  children,
  ...props
}: BadgeProps) => {
  return (
    <div
      {...props}
      className={cn(badgeVariants({ variant, size, radius }), className)}
    >
      {children}
    </div>
  );
};
